import { z } from 'zod';

import { DOCUMENT_STATUSES } from '@/lib/document-status';

// Contratos del alta de documento validados con Zod (ENG-VAL-01). Los ids de BD
// llegan como texto (serializador de bigint); el id del LMS es numérico.

const envelope = <T extends z.ZodType>(data: T) => z.object({ success: z.literal(true), data });

export const registeredEntrySchema = z.object({
  id: z.string(),
  code: z.string(),
  status: z.enum(DOCUMENT_STATUSES),
});
export type RegisteredEntry = z.infer<typeof registeredEntrySchema>;
export const registeredEntryResponseSchema = envelope(registeredEntrySchema);

export const annulResponseSchema = envelope(
  z.object({ entryId: z.string(), status: z.literal('anulado') }),
);

export const lmsLinkResponseSchema = envelope(
  z.object({ id: z.string(), lmsDocumentId: z.string() }),
);

export const lmsSearchRowSchema = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  title: z.string(),
  category: z.string(),
  status: z.string(),
  currentVersion: z.number(),
  hasFile: z.boolean(),
  linkedEntry: z.object({ id: z.string(), code: z.string() }).nullable(),
});
export type LmsSearchRow = z.infer<typeof lmsSearchRowSchema>;
export const lmsSearchResponseSchema = envelope(z.array(lmsSearchRowSchema));

// Propuesta de código (G3-2): esquema del tipo, prefijo y siguiente consecutivo; en un
// tipo libre no hay prefijo ni código.
export const codeProposalSchema = z.object({
  scheme: z.enum(['proceso', 'sap', 'libre']),
  prefix: z.string().nullable(),
  code: z.string().nullable(),
});
export type CodeProposal = z.infer<typeof codeProposalSchema>;
export const codeProposalResponseSchema = envelope(codeProposalSchema);

// Envíos del documento al DMS del LMS (G3-4 creación desde la plantilla, G3-5 baja), tal
// como los trae la ficha. `retryable`: el sistema ya no lo reintenta y Calidad puede.
export const lmsSyncStateSchema = z.object({
  status: z.enum(['pending', 'done', 'failed', 'skipped']),
  failureReason: z.string().nullable(),
  note: z.string().nullable(),
  updatedAt: z.string(),
  retryable: z.boolean(),
});
export type LmsSyncState = z.infer<typeof lmsSyncStateSchema>;
export const lmsSyncSchema = z.object({
  crear: lmsSyncStateSchema.nullable(),
  retirar: lmsSyncStateSchema.nullable(),
});
export type LmsSync = z.infer<typeof lmsSyncSchema>;
export type LmsSyncKind = keyof LmsSync;

export const ownerResponseSchema = envelope(
  z.object({ entryId: z.string(), ownerUserId: z.string().nullable() }),
);
export const retryResponseSchema = envelope(
  z.object({
    entryId: z.string(),
    kind: z.enum(['crear', 'retirar']),
    status: z.literal('pending'),
  }),
);

export type DocumentOrigin = 'interno' | 'externo';

/** Alta: sin `code`, el servidor asigna el siguiente consecutivo del patrón. */
export interface RegisterInput {
  code?: string;
  sapModule?: string;
  title: string;
  documentTypeId: string;
  processAreaId: string;
  origin: DocumentOrigin;
  ownerUserId?: string;
}
